<template>
  <div>
    <HeaderBar />
    <div class="flex">
      <SideBar />
      <div
        class="w-full h-screen flex flex-col bg-gray-900 text-white mt-10 heightfix"
      >
        <!-- Header -->
        <div
          class="flex justify-between items-center px-5 py-4 bg-gray-800 border-b border-gray-700"
        >
          <div class="flex items-center gap-4">
            <h2 class="text-lg font-semibold">Code Editor & Compiler</h2>
            <select
              v-model="selectedLanguage"
              @change="changeLanguage"
              class="px-4 py-2 border border-gray-700 rounded bg-gray-700 text-white text-sm cursor-pointer outline-none transition-all hover:bg-gray-600 hover:border-blue-600"
            >
              <option value="python">Python</option>
              <!-- <option value="java">Java</option> -->
              <option value="c">C</option>
              <option value="cpp">C++</option>
            </select>
          </div>
          <div class="flex items-center gap-4">
            <button
              @click="runCode"
              class="px-5 py-2 rounded text-sm font-semibold transition-all outline-none"
              :class="
                isRunning
                  ? 'bg-gray-600 cursor-not-allowed opacity-60'
                  : 'bg-blue-700 hover:bg-blue-600'
              "
              :disabled="isRunning"
            >
              <span v-if="!isRunning">▶ Run Code</span>
              <span v-else>⏳ Running...</span>
            </button>
            <button
              @click="clearAll"
              class="px-5 py-2 rounded text-sm font-semibold bg-gray-700 hover:bg-gray-600 transition-all outline-none"
            >
              🗑️ Clear
            </button>
          </div>
        </div>
        <div class="flex flex-1 overflow-hidden">
          <div
            class="flex-1 flex flex-col overflow-hidden border-r border-gray-700"
          >
            <div
              class="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-gray-700 text-sm font-semibold"
            >
              <span>📝 Editor</span>
              <span class="px-3 py-1 bg-blue-700 rounded-full text-xs">{{
                currentLanguageName
              }}</span>
            </div>

            <MonacoEditor
              :value="code"
              :language="selectedLanguage"
              height="100%"
              theme="vs-dark"
              :options="editorOptions"
              class="flex-1"
              @change="code = $event"
            ></MonacoEditor>
          </div>

          <!-- Right Side - Output -->
          <div class="flex-1 flex flex-col overflow-hidden">
            <div
              class="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-gray-700 text-sm font-semibold"
            >
              <span>📤 Output</span>
              <span
                v-if="executionTime"
                class="px-3 py-1 bg-green-700 rounded-full text-xs"
                >{{ executionTime }}ms</span
              >
            </div>
            <div class="flex-1 p-4 overflow-y-auto bg-gray-900">
              <div
                v-if="output"
                class="font-mono text-sm whitespace-pre-wrap leading-relaxed"
                :class="isError ? 'text-red-400' : 'text-gray-300'"
              >
                {{ output }}
              </div>
              <div v-else class="text-gray-500 italic text-center mt-12">
                Write your code and click "Run Code" to see output here...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SideBar from "@/components/Student/SideBar.vue";
import HeaderBar from "@/components/Student/Header.vue";
import MonacoEditor from "monaco-editor-vue3";
import axios from "axios";

export default {
  components: {
    SideBar,
    HeaderBar,
    MonacoEditor,
  },
  data() {
    return {
      code: "",
      selectedLanguage: "python",
      currentLanguageName: "Python",
      output: "",
      isError: false,
      isRunning: false,
      executionTime: null,
      editorOptions: {
        theme: "vs-dark",
        fontSize: 14,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        quickSuggestions: false,
        suggestOnTriggerCharacters: false,
        parameterHints: { enabled: false },
        wordBasedSuggestions: false,
        tabCompletion: "off",
      },
    };
  },
  methods: {
    changeLanguage(event) {
      const selectedOption = event.target.options[event.target.selectedIndex];
      this.currentLanguageName = selectedOption.textContent;
      this.output = "";
      this.isError = false;
      this.executionTime = null;
    },

    async runCode() {
      console.log("Run Code Triggered");
      if (!this.code.trim()) {
        this.output = "Error: No code to execute";
        this.isError = true;
        return;
      }

      this.isRunning = true;
      this.isError = false;
      this.output = "";
      const startTime = Date.now();

      const codeData = {
        code: this.code,
        language: this.selectedLanguage,
      };

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/compiler/`,
          codeData
        );

        if (response.data) {
          this.output = response.data.output || "No output";
          this.isError = response.data.error || false;
        }
        this.executionTime = Date.now() - startTime;
      } catch (error) {
        this.output = `Connection Error: ${error.message}\n\nMake sure your Django backend is running and supports ${this.currentLanguageName}.`;
        this.isError = true;
        this.executionTime = Date.now() - startTime;
      } finally {
        this.isRunning = false;
      }
    },

    clearAll() {
      this.code = "";
      this.output = "";
      this.isError = false;
      this.executionTime = null;
    },
  },
};
</script>

<style scoped>
.heightfix {
  height: calc(100vh - 170px) !important;
}

/* Custom scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 10px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #1e1e1e;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #424242;
  border-radius: 5px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #4e4e4e;
}
</style>
